import { useCallback, useRef, useState } from "react";

import { PROOF_COPY } from "./proof.constants.ts";
import { checkLeaves, decodeBase64, parseBundle, toReport } from "./proof.model.ts";
import type { HourView, ProofReport } from "./proof.model.ts";

/**
 * The proof page's controller: take a bundle from a file or a paste, run the
 * model over it, and offer each hour's receipt as a file.
 *
 * THE HASHING IS THIS MACHINE'S. `crypto.subtle` is the browser's own SHA-256,
 * for the reason the Record tab gives: what the page says "recomputes" is what
 * the reader's computer computed, not what ONDO's server asserted. Nothing
 * here makes a request.
 */
const sha256 = async (bytes: Uint8Array): Promise<Uint8Array> =>
  // Copied into a fresh buffer: `digest` wants an ArrayBuffer-backed view and
  // a slice of a shared one is refused by the types before it is by the API.
  new Uint8Array(await crypto.subtle.digest("SHA-256", new Uint8Array(bytes)));

export type ProofView =
  | { kind: "idle" }
  | { kind: "reading"; message: string }
  | { kind: "invalid"; title: string; reason: string }
  | { kind: "checked"; report: ProofReport };

export interface ProofController {
  view: ProofView;
  pasted: string;
  /** The paste box is folded away until asked for: most readers hold a file. */
  pasting: boolean;
  showPaste: () => void;
  typePaste: (text: string) => void;
  checkPaste: () => void;
  checkFile: (file: File | null) => void;
  reset: () => void;
  downloadReceipt: (hour: HourView) => void;
}

export function useProofController(): ProofController {
  const [view, setView] = useState<ProofView>({ kind: "idle" });
  const [pasted, setPasted] = useState("");
  const [pasting, setPasting] = useState(false);
  // Which read is current: the `let active = true` guard, for a page where the
  // stale input is a slow file chosen before a fast paste. Only the latest
  // read may write the view.
  const generation = useRef(0);

  const check = useCallback((text: string) => {
    generation.current += 1;
    const mine = generation.current;
    setView({ kind: "reading", message: PROOF_COPY.reading });

    void (async () => {
      const parsed = parseBundle(text);
      if (parsed.kind === "invalid") {
        if (generation.current === mine)
          setView({ kind: "invalid", title: PROOF_COPY.invalidTitle, reason: parsed.reason });
        return;
      }
      const verdicts = await checkLeaves(parsed.leaves, sha256);
      if (generation.current === mine) setView({ kind: "checked", report: toReport(parsed, verdicts) });
    })();
  }, []);

  const checkFile = useCallback(
    (file: File | null) => {
      if (file === null) return;
      void file.text().then(check);
    },
    [check],
  );

  const checkPaste = useCallback(() => {
    if (pasted.trim().length === 0) return;
    check(pasted);
  }, [check, pasted]);

  const reset = useCallback(() => {
    generation.current += 1;
    setPasted("");
    setPasting(false);
    setView({ kind: "idle" });
  }, []);

  const downloadReceipt = useCallback((hour: HourView) => {
    if (hour.receipt === null || hour.receiptFilename === null) return;
    const blob = new Blob([decodeBase64(hour.receipt)], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = hour.receiptFilename;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  return {
    view,
    pasted,
    pasting,
    showPaste: () => setPasting(true),
    typePaste: setPasted,
    checkPaste,
    checkFile,
    reset,
    downloadReceipt,
  };
}
